/**
 * Gallery Layout Store - Layout transitions and management
 */

import { GALLERY_CONFIG } from '../gallery-config.js';
import { ArrayUtils } from '../utils/array-utils.js';

export function createGalleryLayoutStore() {
  return {
    async switchLayout(new_count) {
      const core_store = Alpine.store('galleryCore');
      const ui_store = Alpine.store('galleryUI');
      const variants_store = Alpine.store('galleryVariants');

      if (new_count === core_store.layout_count) return;

      const expanding = new_count > core_store.layout_count;

      ui_store.setTransitioningLayout(true);

      if (expanding) {
        await this.handleLayoutExpansion(new_count, core_store, ui_store, variants_store);
      } else {
        await this.handleLayoutContraction(new_count, core_store, ui_store);
      }
    },

    async handleLayoutExpansion(new_count, core_store, ui_store, variants_store) {
      core_store.layout_count = new_count;
      const saved_third = typeof core_store.getSavedThirdArtwork === 'function'
        ? core_store.getSavedThirdArtwork()
        : undefined;

      let restored_third = false;

      if (saved_third !== undefined && core_store.selected_artworks.length < 3) {
        core_store.selected_artworks = [...core_store.selected_artworks, saved_third];
        restored_third = true;
      }

      if (core_store.selected_artworks.length < 3) {
        await this.addThirdArtworkFromAvailable(core_store, variants_store);
        restored_third = false;
      }

      const third_artwork = core_store.selected_artworks[2];

      if (restored_third && third_artwork) {
        this.applyVariantToThirdArtwork(third_artwork, core_store, variants_store);
      }

      // Only set third_artwork_visible if we actually have a valid third artwork
      setTimeout(() => {
        const validThirdArtwork = core_store.selected_artworks[2] && core_store.selected_artworks[2].id;
        if (validThirdArtwork) {
          core_store.third_artwork_visible = true;
        }
        ui_store.setTransitioningLayout(false);
      }, GALLERY_CONFIG.TRANSITIONS.GRID_DURATION);
    },

    async handleLayoutContraction(new_count, core_store, ui_store) {
      const third_artwork = core_store.selected_artworks[2];
      if (third_artwork !== undefined && typeof core_store.setSavedThirdArtwork === 'function') {
        core_store.setSavedThirdArtwork(third_artwork);
      }

      core_store.third_artwork_visible = false;
      core_store.layout_count = new_count;

      setTimeout(() => {
        if (core_store.selected_artworks.length > 2) {
          core_store.selected_artworks = core_store.selected_artworks.slice(0, 2);
        }
        ui_store.setTransitioningLayout(false);
      }, GALLERY_CONFIG.TRANSITIONS.FADE_DURATION);
    },

    applyVariantToThirdArtwork(third_artwork, core_store, variants_store) {
      if (core_store.variant_selection.locked) {
        variants_store.applyVariantToArtwork(third_artwork, 2);
      } else if (core_store.variant_selection.size || core_store.variant_selection.frame || core_store.variant_selection.mount) {
        variants_store.applyPreviewVariantToArtwork(third_artwork, 2);

        if (third_artwork.selected_variant) {
          core_store.preview_variants = core_store.preview_variants.filter(v =>
            v.product_id !== third_artwork.id
          );

          core_store.preview_variants.push({
            ...third_artwork.selected_variant,
            product_id: third_artwork.id
          });
        }
      }
    },

    async addThirdArtworkFromAvailable(core_store, variants_store) {
      const filtering_store = Alpine.store('galleryProducts');

      const selected_ids = core_store.selected_artworks.map(artwork => artwork.id);

      const available = ArrayUtils.randomSelectFromFiltered(
        filtering_store.filtered_artworks,
        selected_ids,
        GALLERY_CONFIG.LAYOUT.MIN_CAROUSEL_INDEX
      );

      if (!available) {
        return;
      }

      core_store.selected_artworks = [...core_store.selected_artworks, available];

      const third_artwork = core_store.selected_artworks[2];
      if (!third_artwork) {
        return;
      }

      if (core_store.variant_selection.locked) {
        variants_store.applyVariantToArtwork(third_artwork, 2);
      } else if (core_store.variant_selection.size || core_store.variant_selection.frame || core_store.variant_selection.mount) {
        variants_store.applyPreviewVariantToArtwork(third_artwork, 2);

        if (third_artwork.selected_variant) {
          core_store.preview_variants = core_store.preview_variants.filter(v =>
            v.product_id !== third_artwork.id
          );

          core_store.preview_variants.push({
            ...third_artwork.selected_variant,
            product_id: third_artwork.id
          });
        }
      }
    }
  };
}
